import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Navbar, Offcanvas, Image } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUser } from '../utils/auth';
import ErrorBoundary from './ErrorBoundary';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// role strings are stored/compared verbatim (see CLAUDE.md — no central
// role enum), so this is purely a display-label lookup, not a source of truth.
const ROLE_LABELS = {
  admin: 'Admin',
  masteradmin: 'Master Admin',
  companyadmin: 'Company Admin',
  departmentadmin: 'Department Admin',
  trainingadmin: 'Training Admin',
  trainer: 'Trainer',
  employee: 'Employee',
};

function Layout({ children }) {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // A pending/rejected registration (always role='employee' — see
  // register.php) only ever gets its own restricted profile view — no
  // other route. Layout remounts fresh on every route navigation (each
  // <Route>'s element is a distinct tree, unlike App() itself — see the
  // admin-dashboard staleness fix), so this reliably re-checks on every
  // navigation attempt, not just once at login.
  const isRestrictedEmployee = !!(user && user.role === 'employee' && user.status && user.status !== 'active');

  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user1 = {
    name: 'John Doe',
    avatar: `${BASE_URL}/images/avatar.png`
  };

  const getAvatarUrl = (avatar) => avatar ? `${BASE_URL}/uploads/${avatar}` : `${BASE_URL}/images/avatar.png`;

  useEffect(() => {
    // Fetch user info from local storage
    const readUser = () => setUser(JSON.parse(localStorage.getItem('user')));
    readUser();
    // Re-read after a self-service profile update (e.g. avatar upload) so
    // the topbar reflects it without requiring a fresh login.
    window.addEventListener('user-updated', readUser);
    return () => window.removeEventListener('user-updated', readUser);
  }, []);

  useEffect(() => {
    if (!isRestrictedEmployee) return;
    const ownProfilePath = `/admin/employee/view/${user.id}`;
    if (location.pathname !== ownProfilePath) {
      navigate(ownProfilePath, { replace: true });
    }
  }, [isRestrictedEmployee, user, location.pathname, navigate]);

  const handleLogout = () => {
    // Clear the user session
    localStorage.removeItem('user');
    navigate('/login');
  };

  
  return (
    <Container fluid>
      <Row>
      <Col md={12} className="mb-1 topbar">
          {/* Header for Desktop View */}
          <header className="d-none d-md-flex justify-content-between align-items-center mb-1">
            {user && (
              <span className="role-badge">{ROLE_LABELS[user.role] || user.role}</span>
            )}
            <Nav className="ml-auto ">
              <Nav.Link as={NavLink} to="/">
                <i className="fas fa-home"></i>
              </Nav.Link>
              {user && (
                <>
                <Nav.Link as={NavLink} to="/logout">
                  <i className="fas fa-sign-out-alt"></i>
                </Nav.Link>
                <Nav.Link>
                  <img src={getAvatarUrl(user.avatar)} alt="Avatar" className="rounded-circle avatar" width="30" />
                  {user.name}
                </Nav.Link>
                </>
              )}
            </Nav>
          </header>
        </Col>
      </Row>
      <Row>
        {/* Desktop Sidebar */}
        <Col md={2} className="d-none d-md-block bg-light sidebar">
          <Navbar.Brand href="/">
            <img src={`${BASE_URL}/images/prasarana.png`} alt="Logo" className="img-fluid logo" />
          </Navbar.Brand>
          <Nav className="flex-column">
            <Nav.Link as={NavLink} to="/">Home</Nav.Link>
            {/* <Nav.Link as={NavLink} to="/login">Login</Nav.Link> */}
            {/* <Nav.Link as={NavLink} to="/register">Register</Nav.Link> */}
            {user && !isRestrictedEmployee && (user.role !== 'trainingadmin' && user.role !== 'trainer'
              && user.role !== 'admin' && user.role !== 'masteradmin' && user.role !== 'companyadmin' && user.role !== 'departmentadmin')
            && (
            <Nav.Link as={NavLink} to="/employee/dashboard">User Dashboard</Nav.Link>
            )}
            {/* No separate "My Dashboard" link for trainer/trainingadmin —
                Home ("/") already redirects them straight to their
                dashboard (/trainer/dashboard, /vendor/dashboard — see
                Login.js's mount effect), same reasoning as the admin roles
                below. */}
            {!isRestrictedEmployee && (
            <Nav.Link as={NavLink} to="/trainings">Trainings</Nav.Link>
            )}

            {user && (user.role === 'admin' || user.role === 'masteradmin' || user.role === 'trainingadmin')
            && (
            <Nav.Link as={NavLink} to="/training-provider/course-list">Course List</Nav.Link>
            )}
            {user && (user.role === 'admin' || user.role === 'masteradmin' || user.role === 'companyadmin' || user.role === 'departmentadmin' || user.role === 'trainer' || user.role === 'trainingadmin') && (
            <Nav.Link as={NavLink} to="/reports">Reports</Nav.Link>
            )}
            {!isRestrictedEmployee && (
            <Nav.Link as={NavLink} to="/library">Library</Nav.Link>
            )}
            {user && !isRestrictedEmployee && user.role !== 'trainer' && user.role !== 'trainingadmin' && (
            <Nav.Link as={NavLink} to="/messages">Message Board</Nav.Link>
            )}
            {/* No separate "Admin Dashboard" link — Home ("/") already
                redirects an authenticated admin/masteradmin/companyadmin/
                departmentadmin straight to /admin/dashboard (see the
                getUser() check in Login.js's mount effect). */}
            {/* <Nav.Link as={NavLink} to="/create-user">Create New User</Nav.Link> */}
            {user && (user.role === 'admin' || user.role === 'masteradmin' ||  user.role === 'companyadmin' || user.role === 'departmentadmin' || user.role === 'trainingadmin')
            && (
            <Nav.Link as={NavLink} to="/users">Users</Nav.Link>
            )}
            {user && (user.role === 'admin' || user.role === 'masteradmin' || user.role === 'companyadmin' || user.role === 'departmentadmin') && (
            <Nav.Link as={NavLink} to="/my-trainings">My Trainings</Nav.Link>
            )}
            {user && (
            <Nav.Link as={NavLink} to={`/admin/employee/view/${user.id}`}>My Profile</Nav.Link>
            )}
            {user && (user.role === 'admin' || user.role === 'masteradmin') && (
            <Nav.Link as={NavLink} to="/configuration">Configuration</Nav.Link>
            )}
            <Nav.Link as={NavLink} to="/logout" onClick={handleLogout}>Logout</Nav.Link>
          </Nav>
        </Col>

        {/* Mobile Header */}
        <Col xs={12} className="d-md-none no-print">
          <Navbar bg="light" expand={false}>
            <Container fluid className="d-flex align-items-center">
              <Navbar.Toggle aria-controls="offcanvasNavbar" onClick={handleShow} />
              {user && (
                <span className="role-badge role-badge-mobile ms-2">{ROLE_LABELS[user.role] || user.role}</span>
              )}
              <Navbar.Brand href="/" className="mx-auto">
                <img src={`${BASE_URL}/images/prasarana.png`} alt="Logo" className="img-fluid logo" />
              </Navbar.Brand>
              <Navbar.Offcanvas
                id="offcanvasNavbar"
                aria-labelledby="offcanvasNavbarLabel"
                placement="start"
                show={show}
                onHide={handleClose}
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title id="offcanvasNavbarLabel">Menu</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <Nav className="flex-column">
                    <Nav.Link as={NavLink} to="/" onClick={handleClose}>Home</Nav.Link>
                    {/* <Nav.Link as={NavLink} to="/login" onClick={handleClose}>Login</Nav.Link> */}
                    {/* <Nav.Link as={NavLink} to="/register" onClick={handleClose}>Register</Nav.Link> */}
                    {user && !isRestrictedEmployee && (user.role !== 'trainingadmin' && user.role !== 'trainer'
                      && user.role !== 'admin' && user.role !== 'masteradmin' && user.role !== 'companyadmin' && user.role !== 'departmentadmin')
                    && (
                    <Nav.Link as={NavLink} to="/employee/dashboard" onClick={handleClose}>User Dashboard</Nav.Link>
                    )}
                    {!isRestrictedEmployee && (
                    <Nav.Link as={NavLink} to="/trainings" onClick={handleClose}>Trainings</Nav.Link>
                    )}

                    {user && (user.role === 'admin' || user.role === 'masteradmin' || user.role === 'trainingadmin')
                    && (
                    <Nav.Link as={NavLink} to="/training-provider/course-list" onClick={handleClose}>Course List</Nav.Link>
                    )}
                    {user && (user.role === 'admin' || user.role === 'masteradmin' || user.role === 'companyadmin' || user.role === 'departmentadmin' || user.role === 'trainer' || user.role === 'trainingadmin') && (
                    <Nav.Link as={NavLink} to="/reports" onClick={handleClose}>Reports</Nav.Link>
                    )}
                    {!isRestrictedEmployee && (
                    <Nav.Link as={NavLink} to="/library" onClick={handleClose}>Library</Nav.Link>
                    )}
                    {user && !isRestrictedEmployee && user.role !== 'trainer' && user.role !== 'trainingadmin' && (
                    <Nav.Link as={NavLink} to="/messages" onClick={handleClose}>Message Board</Nav.Link>
                    )}
                    {user && (user.role === 'admin' || user.role === 'masteradmin' ||  user.role === 'companyadmin' || user.role === 'departmentadmin' || user.role === 'trainingadmin')
                    && (
                    <Nav.Link as={NavLink} to="/users" onClick={handleClose}>Users</Nav.Link>
                    )}
                    {user && (user.role === 'admin' || user.role === 'masteradmin' || user.role === 'companyadmin' || user.role === 'departmentadmin') && (
                    <Nav.Link as={NavLink} to="/my-trainings" onClick={handleClose}>My Trainings</Nav.Link>
                    )}
                    {user && (
                    <Nav.Link as={NavLink} to={`/admin/employee/view/${user.id}`} onClick={handleClose}>My Profile</Nav.Link>
                    )}
                    {user && (user.role === 'admin' || user.role === 'masteradmin') && (
                    <Nav.Link as={NavLink} to="/configuration" onClick={handleClose}>Configuration</Nav.Link>
                    )}

                    {user && (
                      <>
                        <Nav.Link onClick={handleClose}>{user.name}</Nav.Link>
                        <Image src={getAvatarUrl(user.avatar)} roundedCircle style={{ width: '30px' }} />
                        <Nav.Link as={NavLink} to="/logout" onClick={handleClose}><i className="fas fa-sign-out-alt"></i> Logout</Nav.Link>
                      </>
                    )}
                  </Nav>
                  
                </Offcanvas.Body>
              </Navbar.Offcanvas>
            </Container>
          </Navbar>
        </Col>

        {/* Main Content */}
        <Col md={10} className="content">
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </Col>
      </Row>
    </Container>
  );
}

export default Layout;
